/**
 * Purpose:
 *   Hook to manage Menu side effects and behaviors.
 *   Handles body scroll lock and keyboard navigation.
 *
 * Notes:
 *   - Prevents body scroll when mobile menu is open
 *   - Closes menu on Escape key press
 */

'use client';

import { useEffect } from 'react';

export interface UseMenuEffectsProps {
  /** Whether the mobile menu is currently open */
  isOpen: boolean;
  /** Callback when mobile menu should close */
  onClose?: () => void;
}

export function useMenuEffects({
  isOpen,
  onClose,
}: UseMenuEffectsProps): void {
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
}
